import { Accessor, JSX, ParentProps, Setter, Signal, createContext, createEffect, createMemo, createSignal, splitProps, useContext } from "solid-js"
import { SetStoreFunction, Store, createStore } from "solid-js/store"
import style from './st-tree.module.css'
import STInput from "../st-input/st-input"
import { createSharedSignal } from "common"

export { default as STTreeNode } from './st-tree-node'

export class TreeContextValue {
    searchValue: Accessor<string>
    setSearchValue: Setter<string>

    expandedIds: Accessor<string[]>

    private manuallyExpandedIds: Accessor<string[]>
    private setManuallyExpandedIds: Setter<string[]>


    selectedId: Accessor<string>
    private setSelectedId: Setter<string>

    private idLabelMap: Record<string, string> = {}
    private setIdLabelMap: SetStoreFunction<Record<string, string>>

    constructor(selectedId?: string, persistenceKey?: string) {
        [this.searchValue, this.setSearchValue] = createSignal('');

        // Use shared signal for persistence if key provided, otherwise use regular signal
        if (persistenceKey) {
            [this.manuallyExpandedIds, this.setManuallyExpandedIds] = createSharedSignal<string[]>(
                `${persistenceKey}.expandedIds`,
                () => []
            );
        } else {
            [this.manuallyExpandedIds, this.setManuallyExpandedIds] = createSignal<string[]>([]);
        }

        [this.selectedId, this.setSelectedId] = createSignal<string>(selectedId ?? '');
        [this.idLabelMap, this.setIdLabelMap] = createStore<Record<string, string>>({});

        this.expandedIds = createMemo(() => {
            if (!this.searchValue()) {
                return this.manuallyExpandedIds()
            }

            const ids = new Set<string>();
            // If we're searching, we want to expand all nodes that match the search
            const matchingIds = Object.keys(this.idLabelMap).filter(id => this.idLabelMap[id].includes(this.searchValue()))

            matchingIds.forEach(id => {
                const parts = id.split('.')
                parts.forEach((_, i) => {
                    ids.add(parts.slice(0, i + 1).join('.'))
                })
                ids.add(id)
            })

            return [...ids]
        });
    }

    toggleExpanded(id: string) {
        const ids = this.expandedIds()
        if (ids.includes(id)) {
            this.setManuallyExpandedIds(ids.filter(k => k !== id))
        } else {
            this.setManuallyExpandedIds((ids) => [...ids, id])
        }
    }

    select(id: string) {
        this.setSelectedId(id)
    }

    setLabel(id: string, label: string) {
        this.setIdLabelMap((map) => ({ ...map, [id]: label }))
    }

    removeLabel(id: string) {
        this.setIdLabelMap((map) => {
            const { [id]: _, ...newMap } = map
            return newMap
        })
    }
}

const TreeContext = createContext<TreeContextValue>()

class STTreeProps {
    context?: TreeContextValue
}

export default function STTree(props: ParentProps<STTreeProps> & JSX.HTMLElementTags['div']) {
    const tree = props.context ?? new TreeContextValue();
    const [local, others] = splitProps(props, ['class', 'context'])


    return (
        <TreeContext.Provider value={tree}>
            <div class={`${style.tree} ${props.class || ''}`} {...others}>
                <div class={style['tree-content']}>
                    <div class={style['search-container']}>
                        <STInput placeholder="Search" icon='fa fa-search' value={[tree.searchValue, tree.setSearchValue]}></STInput>
                    </div>
                    {props.children}
                </div>
            </div>
        </TreeContext.Provider>
    )
}

STTree.defaultProps = new STTreeProps()

export const useTree = () => {
    const tree = useContext(TreeContext)
    if (!tree) {
        throw new Error('TreeContext not found. Make sure the call to useTree occurs somewhere beneath an <STTree> component.')
    }

    return tree
}
